import { getEnv, getAccessToken } from '../../helpers';

export const normalizeSlashUrl = (baseURL = '', url: string) => {
  const normalizedBaseUrl = baseURL.endsWith('/')
    ? baseURL.slice(0, baseURL.length - 1)
    : baseURL;
  const normalizedPath = url.startsWith('/') ? url.slice(1) : url;
  return `${normalizedBaseUrl}/${normalizedPath}`;
};

// a wrapper class that allows
// replacement of axios lib used initially
class Api {
  baseURL: string;
  interceptors: Array<(request: RequestInit) => void>;
  defaultConfig: any;

  constructor(url: string, defaultConfig: any = {}) {
    this.baseURL = url;
    this.interceptors = [];
    this.defaultConfig = defaultConfig;
  }

  addInterceptor(interceptFn: (request: RequestInit) => void) {
    this.interceptors.push(interceptFn);
  }

  appendParamsToURL(url: string, params: any = {}) {
    if (params) {
      const paramsArr = [];
      for (const k of Object.keys(params)) {
        if (params[k] !== null && params[k] !== undefined) {
          paramsArr.push(`${k}=${encodeURIComponent(params[k])}`);
        }
      }
      if (paramsArr.length > 0) url = `${url}?${paramsArr.join('&')}`;
    }
    return url;
  }

  async request(
    url: string,
    body: FormData | any,
    method: string,
    config: any = {},
  ) {
    let headers = config.headers || {};
    url = this.appendParamsToURL(url, config.params);

    if (body) {
      if (typeof body !== 'string') {
        if ('append' in body) {
          // TODO:
          // dealing with FormData
          // should we set Content-Type here?
        } else {
          body = JSON.stringify(body);
          headers = {
            'Content-Type': 'application/json',
            ...headers,
          };
        }
      }
    }

    const requestObj: RequestInit = {
      ...this.defaultConfig,
      method: method,
      headers: new Headers(headers),
      body,
    };

    this.interceptors.forEach((fn) => fn(requestObj));
    const res = await fetch(
      url.startsWith('http') ? url : normalizeSlashUrl(this.baseURL, url),
      requestObj,
    );
    if (res.status >= 400) {
      // sync the original axios reject a promise
      // on error, throw Error here to emulate it
      return Promise.reject(await res.json());
    }

    // get headers obj to return
    // it's used by cart
    const respHeaders: any = {};
    res.headers.forEach((val, key) => (respHeaders[key] = val));
    const data = await res.json();
    return { data, headers: respHeaders };
  }

  async get(url: string, config: any = {}) {
    return await this.request(url, null, 'GET', config);
  }

  async post(url: string, body: FormData | any, config: any = {}) {
    return await this.request(url, body, 'POST', config);
  }

  async patch(url: string, body: any, config: any = {}) {
    return await this.request(url, body, 'PATCH', config);
  }

  async put(url: string, body: any, config: any = {}) {
    return await this.request(url, body, 'PUT', config);
  }

  async delete(url: string, config: any = {}) {
    return await this.request(url, null, 'DELETE', config);
  }
}

export const accessTokenInterceptor = (request: RequestInit) => {
  // some api may get call on the server-side
  if (typeof window === 'undefined') return;

  const accessToken = getAccessToken();
  if (accessToken) {
    (request.headers as Headers).set('Authorization', `Bearer ${accessToken}`);
  }
};

export const policiesApi = new Api(getEnv('NEXT_PUBLIC_BASE_POLICIES_API'));

export const listingApi = new Api(getEnv('BASE_PRODUCT_LISTING_URL'));

export const magentoApi = new Api(getEnv('BASE_EXTRA_SERVICE_URL'));

export const serverApi = new Api('/api');

export const searchApi = new Api(
  typeof window === 'undefined' && getEnv('INTERNAL_SEARCH_URL')
    ? getEnv('INTERNAL_SEARCH_URL')
    : getEnv('BASE_SEARCH_URL'),
);

export const mobioApi = new Api(getEnv('BASE_MOBIO_URL'));

export const orderApi = new Api(getEnv('BASE_COV_URL'));

export const wordpressApi = new Api(getEnv('BASE_WORDPRESS_URL'));

export const ppmApi = new Api(getEnv('BASE_PPM_URL'));

// export const locationApi = new Api(getEnv('BASE_LOCATION_URL'));

// export const loyaltyApi = new Api(getEnv('BASE_LOYALTY_API_URL'));

export const cosmicApi = new Api('https://api.cosmicjs.com/v1/');

export const sellerApi = new Api(getEnv('BASE_SELLER_API'));

// export const warehouseApi = new Api(getEnv('BASE_WAREHOUSE_CONSUMER_API_URL'));

export const masanCCApi = new Api(getEnv('MASAN_CC_URL'));

export const masanBffApi = new Api(getEnv('MASAN_BFF_URL'));

export const masanAdhocApi = new Api(getEnv('MASAN_ADHOC_URL'));

export const masanDealerBffApi = new Api(getEnv('MASAN_DEALER_BFF_URL'));

export const internalCatalogApi = new Api(getEnv('INTERNAL_CATALOG_URL'));

export const internalSellerApi = new Api(getEnv('INTERNAL_SELLER_URL'));

export const publicSettingApi = new Api(getEnv('BASE_PUBLIC_SETTING_API'));
